import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  CircularProgress,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  Stack,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import { addProduct, updateProduct } from '../services/productService';
import { getCategories, addCategory } from '../../companysettings/services/productCategoryService';
import { getSuppliers, addSupplier } from '../../suppliers/services/supplierService';
import { getProductBrands, addProductBrand } from '../../productbrand/services/productBrandService';
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryIcon from "@mui/icons-material/Category";
import QrCodeIcon from "@mui/icons-material/QrCode";
import InventoryIcon from "@mui/icons-material/Inventory";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StyleIcon from "@mui/icons-material/Style";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from 'react-redux';
import { fetchVariantsByProduct, removeVariant } from '../../../app/store/slices/productVariantSlice';
import { 
  fetchVariantAttributes,
  createVariantAttribute,
  updateVariantAttribute,
  deleteVariantAttribute
} from '../../../app/store/slices/variantAttributeSlice';
import VariantFormDialog from '../components/VariantFormDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { createProductVariant } from '../services/productVariantService';
import { bulkCreateInventoryItems } from '../../inventory/services/inventoryItemService';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import EuroIcon from "@mui/icons-material/Euro";

const AddProductPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);

  const generateBarcode = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${timestamp}${random}`;
  };
  
  const navigationData = location.state?.productData;
  
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  
  const [originalFormData, setOriginalFormData] = useState({});
  const [hasFormChanged, setHasFormChanged] = useState(false);
  
  // Form state - ✅ Removed VAT-related fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    imageUrl: '',
    categoryId: '',
    price: '',
    minSalePrice: '',
    costPrice: '',
    averagePrice: '',
    stockQuantity: '',
    minStockLevel: '',
    supplierId: '',
    productBrandId: '',
    modelNumber: '',
    isActive: true,
    isPromotionProduct: false,
    promotionDiscount: '',
    syncToShopify: false,
    hasVariants: false,
    isSerializedProduct: false,
  });

  // Serial numbers for simple products (non-variant products)
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [serialNumberDialogOpen, setSerialNumberDialogOpen] = useState(false);
  const [currentSerialNumber, setCurrentSerialNumber] = useState('');
  const [serialType, setSerialType] = useState('imei'); // 'imei' or 'serial'

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(''); // Server-level error message

  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);

  // Variant management (edit mode)
  const dispatch = useDispatch();
  const { variantsByProduct, loading: variantsLoading } = useSelector((s) => s.productVariants || {});
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [confirmDeleteVariant, setConfirmDeleteVariant] = useState(null);
  const [confirmDeleteDraftVariant, setConfirmDeleteDraftVariant] = useState(null);
  const productVariantsData = isEditMode && id ? (variantsByProduct?.[Number(id)] || null) : null;

  // Draft variants (add mode – stored locally until product is saved)
  const [draftVariants, setDraftVariants] = useState([]);

  // Variant attributes management (global definitions available for all variants)
  const [attrDialogOpen, setAttrDialogOpen] = useState(false);
  const globalVariantAttributes = useSelector((s) => s.variantAttributes?.attributes || []);
  const variantAttrsLoading = useSelector((s) => s.variantAttributes?.loading || false);
  const variantAttrsError = useSelector((s) => s.variantAttributes?.error || null);
  const lastFetched = useSelector((s) => s.variantAttributes?.lastFetched || null);
  const [variantAttributes, setVariantAttributes] = useState([]);
  const [attrEditForm, setAttrEditForm] = useState({ id: null, name: '', values: '' });
  const [newAttrForm, setNewAttrForm] = useState({ name: '', values: '' });
  const hasFetchedAttributesRef = useRef(false);

  // Load variant attributes from Redux when 'hasVariants' is toggled ON
  useEffect(() => {
    if (formData.hasVariants) {
      if (globalVariantAttributes.length > 0) {
        setVariantAttributes(globalVariantAttributes);
        hasFetchedAttributesRef.current = true;
      } else if (!hasFetchedAttributesRef.current && !variantAttrsLoading && !variantAttrsError) {
        // Only fetch once: if not already fetched, not currently loading, and no error
        hasFetchedAttributesRef.current = true;
        dispatch(fetchVariantAttributes());
      }
    } else {
      // Reset fetch tracking when hasVariants is toggled off
      hasFetchedAttributesRef.current = false;
    }
  }, [formData.hasVariants, globalVariantAttributes.length, dispatch]);

  // Sync local state with Redux
  useEffect(() => {
    console.log('Syncing variantAttributes from Redux:', globalVariantAttributes);
    setVariantAttributes(globalVariantAttributes);
  }, [globalVariantAttributes]);

  // Serial input state for non-variant serialized products
  const [serialInput, setSerialInput] = useState('');

  // Current variant being created/edited (form state)
  const [currentVariant, setCurrentVariant] = useState({
    variantName: '',
    sku: '',
    barCode: '',
    salePrice: '',
    minSalePrice: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    attributes: '',
    imageUrl: '',
    imeiItems: [],
    serialType: 'imei' // 'imei' or 'serial'
  });
  const [showVariantSerialInput, setShowVariantSerialInput] = useState(false);
  const [variantSerialInput, setVariantSerialInput] = useState('');
  const [isEditingVariant, setIsEditingVariant] = useState(false);
  const [editVariantIndex, setEditVariantIndex] = useState(null);

  // Reset current variant form
  const resetCurrentVariant = () => {
    setCurrentVariant({
      variantName: '',
      sku: '',
      barCode: '',
      salePrice: '',
      minSalePrice: '',
      costPrice: '',
      stockQuantity: '',
      minStockLevel: '',
      attributes: '',
      imageUrl: '',
      imeiItems: [],
      serialType: 'imei'
    });
    setShowVariantSerialInput(false);
    setVariantSerialInput('');
    setIsEditingVariant(false);
    setEditVariantIndex(null);
  };

  // Add or update variant
  const handleAddOrUpdateVariant = () => {
    if (isEditingVariant && editVariantIndex !== null) {
      // Update existing variant
      setDraftVariants(prev => prev.map((v, idx) => 
        idx === editVariantIndex ? { ...currentVariant } : v
      ));
      toast.success('Variant updated successfully');
    } else {
      // Add new variant
      setDraftVariants(prev => [...prev, { ...currentVariant }]);
      toast.success('Variant added successfully');
    }
    resetCurrentVariant();
  };

  // Edit variant
  const handleEditVariant = (idx) => {
    setCurrentVariant({ ...draftVariants[idx] });
    setIsEditingVariant(true);
    setEditVariantIndex(idx);
    setShowVariantSerialInput(draftVariants[idx].imeiItems?.length > 0);
  };

  // Delete variant
  const handleDeleteVariant = (idx) => {
    setConfirmDeleteDraftVariant(idx);
  };

  // Confirm delete draft variant
  const confirmDeleteDraft = () => {
    if (confirmDeleteDraftVariant !== null) {
      setDraftVariants(prev => prev.filter((_, i) => i !== confirmDeleteDraftVariant));
      toast.success('Variant deleted');
      if (isEditingVariant && editVariantIndex === confirmDeleteDraftVariant) {
        resetCurrentVariant();
      }
      setConfirmDeleteDraftVariant(null);
    }
  };

  // Add serial number to current variant
  const handleAddVariantSerial = () => {
    const trimmed = variantSerialInput.trim();
    if (!trimmed) return;
    
    // Check if serial exists in current variant
    if (currentVariant.imeiItems.some(item => (item.value || item.imeiNumber || item) === trimmed)) {
      toast.warning(`${currentVariant.serialType === 'imei' ? 'IMEI' : 'Serial number'} already added to this variant`);
      setVariantSerialInput(''); // Clear input on duplicate
      return;
    }
    
    // Check if serial exists in other draft variants
    const existsInOtherVariants = draftVariants.some(v => 
      v.imeiItems?.some(item => (item.value || item.imeiNumber || item) === trimmed)
    );
    if (existsInOtherVariants) {
      toast.error(`This ${currentVariant.serialType === 'imei' ? 'IMEI' : 'serial number'} is already used in another variant`);
      setVariantSerialInput(''); // Clear input on duplicate
      return;
    }
    
    // Check if editing a variant - exclude the current variant being edited
    if (isEditingVariant && editVariantIndex !== null) {
      const otherVariants = draftVariants.filter((_, idx) => idx !== editVariantIndex);
      const existsInOthers = otherVariants.some(v => 
        v.imeiItems?.some(item => (item.value || item.imeiNumber || item) === trimmed)
      );
      if (existsInOthers) {
        toast.error(`This ${currentVariant.serialType === 'imei' ? 'IMEI' : 'serial number'} is already used in another variant`);
        setVariantSerialInput(''); // Clear input on duplicate
        return;
      }
    }
    
    setCurrentVariant(prev => ({
      ...prev,
      imeiItems: [...prev.imeiItems, { value: trimmed, type: currentVariant.serialType }]
    }));
    setVariantSerialInput('');
    toast.success(`${currentVariant.serialType === 'imei' ? 'IMEI' : 'Serial number'} added`);
  };

  // Remove serial number from current variant
  const handleRemoveVariantSerial = (serialIdx) => {
    setCurrentVariant(prev => ({
      ...prev,
      imeiItems: prev.imeiItems.filter((_, i) => i !== serialIdx)
    }));
    toast.success('Serial number removed');
  };

  // Check if current variant form is valid
  const isCurrentVariantValid = () => {
    return currentVariant.variantName.trim() && 
           currentVariant.sku.trim() && 
           currentVariant.salePrice && 
           currentVariant.costPrice;
  };

  // Legacy variant functions removed - now using currentVariant form approach

  const pendingCategoryNameRef = useRef('');

  const checkIfFormChanged = (currentData, originalData) => {
    const currentStr = JSON.stringify(currentData);
    const originalStr = JSON.stringify(originalData);
    return currentStr !== originalStr;
  };

  const populateFormData = (product) => {
    const populatedData = {
      name: product.Name || product.ProductName || product.name || '',
      description: product.Description || product.description || '',
      sku: product.SKU || product.Sku || product.sku || '',
      barcode: product.Barcode || product.BarCode || product.barcode || '',
      imageUrl: product.ImageUrl || product.imageUrl || '',
      categoryId: product.ProductCategoryId ? String(product.ProductCategoryId) : '',
      price: product.SalePrice ? String(product.SalePrice) : '',
      minSalePrice: product.MinSalePrice ? String(product.MinSalePrice) : '',
      costPrice: product.CostPrice ? String(product.CostPrice) : '',
      averagePrice: product.AveragePrice ? String(product.AveragePrice) : '',
      stockQuantity: product.StockQuantity || product.StockQuanity ? String(product.StockQuantity || product.StockQuanity) : '',
      minStockLevel: product.MinStockLevel ? String(product.MinStockLevel) : '',
      supplierId: product.SupplierId ? String(product.SupplierId) : '',
      productBrandId: product.ProductBrandId ? String(product.ProductBrandId) : (product.BrandId ? String(product.BrandId) : ''),
      modelNumber: product.ModelNumber || '',
      isActive: product.IsActive !== undefined ? product.IsActive : true,
      isPromotionProduct: product.IsPromotionProduct || product.isPromotionProduct || false,
      promotionDiscount: product.PromotionPercentage ? String(product.PromotionPercentage) : '',
      syncToShopify: product.SyncToShopify || false
    };
    
    // Store category name for fallback lookup once categories are loaded
    pendingCategoryNameRef.current =
      product.ProductCategory?.CategoryName || product.CategoryName || '';

    setFormData(populatedData);
    if (isEditMode) {
      setOriginalFormData({ ...populatedData });
      setHasFormChanged(false);
    }
  };

  const loadProductDataFromAPI = async () => {
    if (!id) {
      toast.error('Product ID is missing');
      navigate('/products');
      return;
    }

    setDataLoading(true);
    try {
      const response = await axiosInstance.get(`Products/${id}`);
      
      if (response.data.status === 'Success' && response.data.payload) {
        populateFormData(response.data.payload);
      } else {
        throw new Error(response.data.message || 'Product not found');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Product not found');
      } else {
        toast.error('Failed to load product data');
      }
      navigate('/products');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode) return;

    if (navigationData) {
      populateFormData(navigationData);
    } else {
      loadProductDataFromAPI();
    }
  }, [isEditMode, navigationData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        try {
          const categoriesResponse = await getCategories();
          const categoryData = categoriesResponse?.payload || categoriesResponse || [];
          setCategories(Array.isArray(categoryData) ? categoryData : []);
        } catch (catError) {
          console.error('Error fetching categories:', catError);
          setCategories([]);
        }

        try {
          const suppliersResponse = await getSuppliers();
          const supplierData = suppliersResponse?.payload || suppliersResponse || [];
          setSuppliers(Array.isArray(supplierData) ? supplierData : []);
        } catch (suppError) {
          console.error('Error fetching suppliers:', suppError);
          setSuppliers([]);
        }

        try {
          const brandsResponse = await getProductBrands();
          const brandData = brandsResponse?.payload || brandsResponse || [];
          setBrands(Array.isArray(brandData) ? brandData : []);
        } catch (brandError) {
          console.error('Error fetching brands:', brandError);
          setBrands([]);
        }
        
      } catch (error) {
        console.error('Critical error during data fetch:', error);
        setCategories([]);
        setSuppliers([]);
        setBrands([]);
      }
    };

    fetchData();
  }, []);

  // Fallback: if category wasn't set by ID, find it by CategoryName once categories load
  useEffect(() => {
    if (!isEditMode) return;
    if (!categories.length) return;
    if (formData.categoryId) return;
    const name = pendingCategoryNameRef.current;
    if (!name) return;
    const match = categories.find(c => c.CategoryName === name);
    if (match) {
      const idStr = String(match.Id);
      setFormData(prev => ({ ...prev, categoryId: idStr }));
      setOriginalFormData(prev => ({ ...prev, categoryId: idStr }));
    }
  }, [categories]);

  // Fetch variants when editing an existing product
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchVariantsByProduct(Number(id)));
    }
  }, [isEditMode, id]);

  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const calculateFormValidity = () => {
    // Required fields
    if (!formData.name.trim()) return false;
    if (formData.name.length > 300) return false; // ✅ Updated limit
    
    if (!formData.categoryId) return false;
    if (!formData.supplierId) return false;
    
    // Price fields - only required if hasVariants is false
    if (!formData.hasVariants) {
      if (!formData.price || parseFloat(formData.price) < 0 || parseFloat(formData.price) > 100000) return false;
      if (!formData.costPrice || parseFloat(formData.costPrice) < 0 || parseFloat(formData.costPrice) > 100000) return false;
      if (!formData.stockQuantity || parseFloat(formData.stockQuantity) < 0 || parseFloat(formData.stockQuantity) > 100000) return false;
    }
    
    // Optional fields validation
    if (formData.sku && (formData.sku.length < 3 || formData.sku.length > 30)) return false;
    if (formData.barcode && (formData.barcode.length < 3 || formData.barcode.length > 30)) return false;
    if (formData.description && formData.description.length > 500) return false; // ✅ Updated limit
    if (formData.modelNumber && formData.modelNumber.length > 200) return false; // ✅ Updated limit
    
    if (formData.imageUrl) {
      if (formData.imageUrl.length > 300) return false;
      try {
        new URL(formData.imageUrl);
      } catch {
        return false;
      }
    }
    
    if (formData.minSalePrice) {
      const minPrice = parseFloat(formData.minSalePrice);
      if (minPrice < 0 || minPrice > 100000) return false;
    }
    
    if (formData.minStockLevel) {
      const minStock = parseFloat(formData.minStockLevel);
      if (minStock < 0 || minStock > 100000) return false;
    }
    
    if (formData.isPromotionProduct) {
      const discount = parseFloat(formData.promotionDiscount);
      if (!formData.promotionDiscount || discount < 0 || discount > 100) return false;
    }
    
    return true;
  };

  useEffect(() => {
    const isValid = calculateFormValidity();
    setIsFormValid(isValid);
  }, [
    formData.name,
    formData.sku,
    formData.categoryId,
    formData.price,
    formData.costPrice,
    formData.stockQuantity,
    formData.minStockLevel,
    formData.description,
    formData.modelNumber,
    formData.isPromotionProduct,
    formData.promotionDiscount,
    formData.hasVariants,
    formData.supplierId
  ]);

  const validateAndShowErrors = () => {
    const newErrors = {};

    // Name validation - ✅ Updated limit
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    } else if (formData.name.length > 300) {
      newErrors.name = 'Name cannot exceed 300 characters';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (!formData.supplierId) {
      newErrors.supplierId = 'Supplier is required';
    }

    // Price validations - ONLY required if hasVariants is false
    if (!formData.hasVariants) {
      if (!formData.price) {
        newErrors.price = 'Sale price is required';
      } else {
        const price = parseFloat(formData.price);
        if (price < 0 || price > 100000) {
          newErrors.price = 'Sale price must be between 0 and 100,000';
        }
      }

      if (!formData.costPrice) {
        newErrors.costPrice = 'Cost price is required';
      } else {
        const costPrice = parseFloat(formData.costPrice);
        if (costPrice < 0 || costPrice > 100000) {
          newErrors.costPrice = 'Cost price must be between 0 and 100,000';
        }
      }

      if (!formData.stockQuantity) {
        newErrors.stockQuantity = 'Stock quantity is required';
      } else {
        const stock = parseFloat(formData.stockQuantity);
        if (stock < 0 || stock > 100000) {
          newErrors.stockQuantity = 'Stock quantity must be between 0 and 100,000';
        }
      }
    }

    if (formData.sku && (formData.sku.length < 3 || formData.sku.length > 30)) {
      newErrors.sku = 'SKU must be between 3-30 characters';
    }

    if (formData.barcode && (formData.barcode.length < 3 || formData.barcode.length > 30)) {
      newErrors.barcode = 'Barcode must be between 3-30 characters';
    }

    // ✅ Updated description limit
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    // ✅ Updated model number limit
    if (formData.modelNumber && formData.modelNumber.length > 200) {
      newErrors.modelNumber = 'Model number cannot exceed 200 characters';
    }

    if (formData.imageUrl) {
      if (formData.imageUrl.length > 300) {
        newErrors.imageUrl = 'Image URL cannot exceed 300 characters';
      } else {
        try {
          const url = new URL(formData.imageUrl);
          if (!['http:', 'https:'].includes(url.protocol)) {
            newErrors.imageUrl = 'URL must start with http:// or https://';
          }
        } catch {
          newErrors.imageUrl = 'Please enter a valid URL (e.g., https://example.com/image.jpg)';
        }
      }
    }

    if (formData.minSalePrice) {
      const minPrice = parseFloat(formData.minSalePrice);
      if (minPrice < 0 || minPrice > 100000) {
        newErrors.minSalePrice = 'Min sale price must be between 0 and 100,000';
      }
    }

    if (formData.minStockLevel) {
      const minStock = parseFloat(formData.minStockLevel);
      if (minStock < 0 || minStock > 100000) {
        newErrors.minStockLevel = 'Min stock level must be between 0 and 100,000';
      }
    }

    if (formData.isPromotionProduct) {
      const discount = parseFloat(formData.promotionDiscount);
      if (!formData.promotionDiscount || discount < 0 || discount > 100) {
        newErrors.promotionDiscount = 'Promotion percentage must be between 0-100%';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSingleField = (fieldName, value) => {
    if (!hasAttemptedSubmit) return;

    let error = '';
    
    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          error = 'Product name is required';
        } else if (value.length > 300) { // ✅ Updated limit
          error = 'Name cannot exceed 300 characters';
        }
        break;
      case 'categoryId':
        if (!value) {
          error = 'Category is required';
        }
        break;
      case 'supplierId':
        if (!value) {
          error = 'Supplier is required';
        }
        break;
      case 'price':
        if (!formData.hasVariants) {
          if (!value) {
            error = 'Sale price is required';
          } else {
            const price = parseFloat(value);
            if (price < 0 || price > 100000) {
              error = 'Sale price must be between 0 and 100,000';
            }
          }
        }
        break;
      case 'costPrice':
        if (!formData.hasVariants) {
          if (!value) {
            error = 'Cost price is required';
          } else {
            const costPrice = parseFloat(value);
            if (costPrice < 0 || costPrice > 100000) {
              error = 'Cost price must be between 0 and 100,000';
            }
          }
        }
        break;
      case 'stockQuantity':
        if (!formData.hasVariants) {
          if (!value) {
            error = 'Stock quantity is required';
          } else {
            const stock = parseFloat(value);
            if (stock < 0 || stock > 100000) {
              error = 'Stock quantity must be between 0 and 100,000';
            }
          }
        }
        break;
      case 'sku':
        if (value && (value.length < 3 || value.length > 30)) {
          error = 'SKU must be between 3-30 characters';
        }
        break;
      case 'barcode':
        if (value && (value.length < 3 || value.length > 30)) {
          error = 'Barcode must be between 3-30 characters';
        }
        break;
      case 'description':
        if (value && value.length > 500) { // ✅ Updated limit
          error = 'Description cannot exceed 500 characters';
        }
        break;
      case 'imageUrl':
        if (value) {
          if (value.length > 300) {
            error = 'Image URL cannot exceed 300 characters';
          } else {
            try {
              const url = new URL(value);
              if (!['http:', 'https:'].includes(url.protocol)) {
                error = 'URL must start with http:// or https://';
              }
            } catch {
              error = 'Please enter a valid URL (e.g., https://example.com/image.jpg)';
            }
          }
        }
        break;
      case 'minSalePrice':
        if (value) {
          const minPrice = parseFloat(value);
          if (minPrice < 0 || minPrice > 100000) {
            error = 'Min sale price must be between 0 and 100,000';
          }
        }
        break;
      case 'minStockLevel':
        if (value) {
          const minStock = parseFloat(value);
          if (minStock < 0 || minStock > 100000) {
            error = 'Min stock level must be between 0 and 100,000';
          }
        }
        break;
      case 'promotionDiscount':
        if (formData.isPromotionProduct) {
          const discount = parseFloat(value);
          if (!value || discount < 0 || discount > 100) {
            error = 'Promotion percentage must be between 0-100%';
          }
        }
        break;
      case 'modelNumber':
        if (value && value.length > 200) { // ✅ Updated limit
          error = 'Model number cannot exceed 200 characters';
        }
        break;
      case 'averagePrice':
        if (value) {
          const avgPrice = parseFloat(value);
          if (avgPrice < 0 || avgPrice > 100000) {
            error = 'Average price must be between 0 and 100,000';
          }
        }
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      if (isEditMode) {
        const formChanged = checkIfFormChanged(updated, originalFormData);
        setHasFormChanged(formChanged);
      }
      
      return updated;
    });
    
    if (errors[name] && touchedFields[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear server error when user makes changes
    if (serverError) {
      setServerError('');
    }
  };

  const handleFieldBlur = (event) => {
    const { name, value } = event.target;
    
    if (hasUserInteracted || hasAttemptedSubmit) {
      validateSingleField(name, value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setHasAttemptedSubmit(true);
    setServerError(''); // Clear any previous server errors
    
    if (!validateAndShowErrors()) {
      setServerError('Please fill in all required fields');
      
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    if (isEditMode && !hasFormChanged) {
      toast.warning('No changes detected. Please modify the form to update.');
      return;
    }

    // Extra defensive check for required fields before API call
    if (!formData.name || !formData.name.trim()) {
      setServerError('Product name is required');
      setErrors(prev => ({ ...prev, name: 'Product name is required' }));
      
      // Scroll to name field
      const nameField = document.querySelector('[name="name"]');
      if (nameField) {
        nameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        nameField.focus();
      }
      return;
    }

    setLoading(true);

    try {
      console.log('🔍 Form data before building payload:', {
        name: formData.name,
        nameLength: formData.name?.length,
        nameTrimmed: formData.name?.trim(),
        hasVariants: formData.hasVariants
      });

      // ✅ Removed all VAT-related fields
      const productData = {
        Name: formData.name.trim(),
        SKU: formData.hasVariants ? null : (formData.sku.trim() || null),
        BarCode: formData.hasVariants ? null : (formData.barcode.trim() || null),
        Description: formData.description.trim() || null,
        ImageUrl: formData.imageUrl.trim() || null,
        SalePrice: formData.hasVariants ? 0 : parseFloat(formData.price || 0),
        MinSalePrice: formData.hasVariants ? 0 : (formData.minSalePrice ? parseFloat(formData.minSalePrice) : 0),
        CostPrice: formData.hasVariants ? 0 : parseFloat(formData.costPrice || 0),
        StockQuantity: formData.hasVariants ? 0 : parseFloat(formData.stockQuantity || 0),
        MinStockLevel: formData.hasVariants ? 0 : (formData.minStockLevel ? parseFloat(formData.minStockLevel) : 0),
        IsPromotionProduct: formData.isPromotionProduct,
        PromotionPercentage: formData.isPromotionProduct 
          ? parseFloat(formData.promotionDiscount) 
          : 0,
        ProductCategoryId: parseInt(formData.categoryId),
        SupplierId: parseInt(formData.supplierId),
        ProductBrandId: formData.productBrandId ? parseInt(formData.productBrandId) : null,
        ModelNumber: formData.modelNumber?.trim() || null,
        AveragePrice: formData.averagePrice ? parseFloat(formData.averagePrice) : 0,
        IsActive: formData.isActive,
        IsWishlistProduct: false,
        SyncToShopify: formData.syncToShopify,
        
        // NEW: Variant and serialization fields
        HasVariants: formData.hasVariants,
        IsSerializedProduct: formData.isSerializedProduct && !formData.hasVariants,
        
        // Convert serial numbers to InventoryItems for simple products (non-variant)
        InventoryItems: (!formData.hasVariants && formData.isSerializedProduct && serialNumbers.length > 0) 
          ? serialNumbers.map(sn => {
              const serialObj = typeof sn === 'object' ? sn : { value: sn, type: 'imei' };
              return {
                IMEI: serialObj.type === 'imei' ? serialObj.value : null,
                SerialNumber: serialObj.type === 'serial' ? serialObj.value : null
              };
            })
          : null,
        
        Variants: formData.hasVariants ? draftVariants.map(v => {
          // Ensure attributes is always a string (JSON stringified if object)
          let attributesStr = null;
          if (v.attributes) {
            if (typeof v.attributes === 'string') {
              attributesStr = v.attributes;
            } else if (typeof v.attributes === 'object') {
              attributesStr = JSON.stringify(v.attributes);
            }
          }
          
          // Remove frontend-only fields before sending to backend
          const { imeiItems, serialType, ...rest } = v;
          
          return {
            VariantName: v.variantName,
            SKU: v.sku,
            BarCode: v.barCode || null,
            Attributes: attributesStr,
            SalePrice: parseFloat(v.salePrice),
            MinSalePrice: v.minSalePrice ? parseFloat(v.minSalePrice) : 0,
            CostPrice: parseFloat(v.costPrice),
            StockQuantity: v.stockQuantity ? parseInt(v.stockQuantity, 10) : 0,
            MinStockLevel: v.minStockLevel ? parseInt(v.minStockLevel, 10) : 0,
            ImageUrl: v.imageUrl || null,
            TrackSerialNumbers: imeiItems?.length > 0,
            // Convert variant serial numbers to InventoryItems
            InventoryItems: imeiItems?.length > 0
              ? imeiItems.map(item => {
                  const serialObj = typeof item === 'object' ? item : { value: item, type: 'imei' };
                  return {
                    IMEI: (serialObj.type === 'imei') ? (serialObj.value || serialObj.imeiNumber || item) : null,
                    SerialNumber: (serialObj.type === 'serial') ? (serialObj.value || serialObj.imeiNumber || item) : null
                  };
                })
              : null
          };
        }) : []
      };

      console.log('📦 Product payload built:', {
        Name: productData.Name,
        NameType: typeof productData.Name,
        NameLength: productData.Name?.length,
        HasVariants: productData.HasVariants,
        VariantsCount: productData.Variants?.length || 0,
        fullPayload: productData
      });

      let response;
      if (isEditMode) {
        const updatePayload = {
          ...productData,
          Id: parseInt(id)
        };
        
        console.log('🔄 Update payload:', updatePayload);
        response = await updateProduct(updatePayload);
        toast.success('Product updated successfully!');
      } else {
        console.log('➕ Add product payload:', productData);
        response = await addProduct(productData);
        const newProductId = response?.payload?.Id || response?.payload?.id || response?.Id || response?.id;
        // Create any draft variants that were added before saving
        if (draftVariants.length > 0 && newProductId) {
          const results = await Promise.allSettled(
            draftVariants.map(async (v) => {
              // Separate imeiItems from the variant API payload
              const { imeiItems, ...variantData } = v;
              const variantRes = await createProductVariant({ ...variantData, productId: newProductId });
              const newVariantId =
                variantRes?.payload?.id || variantRes?.payload?.Id ||
                variantRes?.id || variantRes?.Id;
              // Create inventory items (IMEIs) if any were entered
              if (imeiItems?.length > 0 && newVariantId) {
                await bulkCreateInventoryItems({
                  productVariantId: newVariantId,
                  items: imeiItems,
                  purchaseDate: new Date().toISOString(),
                });
              }
              return variantRes;
            })
          );
          const failed = results.filter(r => r.status === 'rejected').length;
          const succeeded = draftVariants.length - failed;
          const totalImei = draftVariants.reduce((s, v) => s + (v.imeiItems?.length || 0), 0);
          if (failed > 0) {
            toast.warning(`Product created, ${succeeded} variant(s) saved, ${failed} failed. Edit product to retry.`);
          } else if (totalImei > 0) {
            toast.success(`Product, ${draftVariants.length} variant(s), and ${totalImei} inventory item(s) created!`);
          } else {
            toast.success(`Product and ${draftVariants.length} variant(s) created successfully!`);
          }
        } else {
          toast.success('Product added successfully!');
        }
      }
      
      navigate('/products', {
        state: { 
          message: isEditMode ? 'Product updated successfully' : 'Product added successfully',
          productId: response?.payload?.Id || response?.Id || response?.id || id
        }
      });

    } catch (error) {
      console.error('❌ Error saving product:', error);
      console.error('❌ Error response:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        errors: error.response?.data?.errors,
        message: error.response?.data?.message
      });
      
      // Display all backend errors at the bottom of the form, no toasts
      const errorMessage = error.response?.data?.message || '';
      
      if (errorMessage) {
        setServerError(errorMessage);
      } else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setServerError('Please fix the form errors');
      } else {
        // Generic fallback error
        setServerError(isEditMode ? 'Failed to update product' : 'Failed to add product');
      }
      
      // Scroll to the error message
      setTimeout(() => {
        const errorElement = document.getElementById('server-error-message');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleResetForm = () => {
    setFormData({ ...originalFormData });
    setHasFormChanged(false);
    setErrors({});
    setServerError('');
    setTouchedFields({});
    setHasUserInteracted(false);
    setHasAttemptedSubmit(false);
    toast.info('Form reset to original values');
  };

  const isButtonDisabled = useMemo(() => {
    return loading || (isEditMode && !hasFormChanged);
  }, [loading, isEditMode, hasFormChanged]);

  const shouldShowError = (fieldName) => {
    return hasAttemptedSubmit && !!errors[fieldName];
  };

  if (dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 400,
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading product data...
          </Typography>
        </Box>
      </Container>
    );
  }

  const handleCancel = () => {
    const hasAnyData = Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'boolean') return value !== true;
      return value !== '';
    });

    if ((isEditMode && hasFormChanged) || (!isEditMode && hasAnyData)) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/products');
      }
    } else {
      navigate('/products');
    }
  };

  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newSupplier, setNewSupplier] = useState({ name: '', contactPerson: '', email: '', phone: '', address: '' });
  const [newBrand, setNewBrand] = useState({ 
    BrandName: '', 
    Description: '', 
    LogoUrl: '', 
    Website: '' 
  });

  const [supplierFormTouched, setSupplierFormTouched] = useState(false);
  const [brandFormTouched, setBrandFormTouched] = useState(false);

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      const response = await addCategory({
        CategoryName: newCategory.name
      });

      if (response.status === 'Success' || response) {
        const addedCategory = response.payload || response;
        toast.success('Category added successfully');
        
        const categoriesResponse = await getCategories();
        const categoriesData = categoriesResponse.payload || [];
        setCategories(categoriesData);
        
        setFormData(prev => ({ ...prev, categoryId: String(addedCategory.Id) }));
        setOpenCategoryDialog(false);
        setNewCategory({ name: '' });
      }
    } catch (error) {
      toast.error('Failed to add category');
      console.error(error);
    }
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      setSupplierFormTouched(true);
      toast.error('Supplier name is required');
      return;
    }

    try {
      const supplierData = {
        Name: newSupplier.name.trim(),
        Contactperson: newSupplier.contactPerson?.trim() || null,
        Email: newSupplier.email?.trim() || null,
        MobileNumber: newSupplier.phone?.trim() || null,
        Street: newSupplier.address?.trim() || null,
        StreetNumber: null,
        PostCode: null,
        City: null,
        Country: null,
        BTWNumber: null,
        Notes: null
      };

      const response = await addSupplier(supplierData);
      
      toast.success('Supplier added successfully');
      
      const suppliersResponse = await getSuppliers();
      const suppliersData = suppliersResponse?.payload || suppliersResponse || [];
      
      if (Array.isArray(suppliersData)) {
        setSuppliers(suppliersData);
      } else {
        setSuppliers([]);
      }
      
      const addedSupplierId = response.Id || response.id || response.payload?.Id;
      if (addedSupplierId) {
        setFormData(prev => ({ ...prev, supplierId: String(addedSupplierId) }));
      }
      
      setOpenSupplierDialog(false);
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
      setSupplierFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding supplier:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error('Please fix the supplier form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to add supplier. Please try again.');
      }
    }
  };

  const handleAddBrand = async () => {
    if (!newBrand.BrandName.trim()) {
      setBrandFormTouched(true);
      toast.error('Brand name is required');
      return;
    }

    try {
      const brandData = {
        BrandName: newBrand.BrandName.trim(),
        Description: newBrand.Description?.trim() || null,
        LogoUrl: newBrand.LogoUrl?.trim() || null,
        Website: newBrand.Website?.trim() || null
      };

      const response = await addProductBrand(brandData);
      
      toast.success('Brand added successfully');
      
      const brandsResponse = await getProductBrands();
      const brandsData = brandsResponse?.payload || brandsResponse || [];
      
      if (Array.isArray(brandsData)) {
        setBrands(brandsData);
      } else {
        setBrands([]);
      }
      
      const addedBrandId = response.Id || response.id || response.payload?.Id;
      if (addedBrandId) {
        setFormData(prev => ({ ...prev, productBrandId: String(addedBrandId) }));
      }
      
      setOpenBrandDialog(false);
      setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
      setBrandFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding brand:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error('Please fix the brand form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to add brand. Please try again.');
      }
    }
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenCategoryDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  const handleSupplierChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenSupplierDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  const handleBrandChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenBrandDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  const categoryOptions = [
    { id: 'ADD_NEW', label: 'Add New Category', isAddNew: true },
    ...categories.map(c => ({ id: String(c.Id), label: c.CategoryName || '' })),
  ];
  const supplierOptions = [
    { id: 'ADD_NEW', label: 'Add New Supplier', isAddNew: true },
    ...suppliers.map(s => ({ id: String(s.Id), label: s.Name || s.SupplierName || 'Unknown Supplier' })),
  ];
  const brandOptions = [
    { id: 'ADD_NEW', label: 'Add New Brand', isAddNew: true },
    ...brands.map(b => ({ id: String(b.Id), label: b.BrandName || 'Unknown Brand' })),
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
      {/* ✅ Dynamic Header */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: 'left', 
            mb: 0.5 
          }}
        >
          {isEditMode ? t('edit_product') : t('add_product')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditMode ? t('update_product_subtitle') || 'Update the product information below' : t('add_product_subtitle') || 'Fill in the product information below'}
        </Typography>
        {/* ✅ Show data source indicator in edit mode */}
        {isEditMode && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
            {navigationData ? '📦 Data loaded from cache' : '🌐 Data loaded from server'}
          </Typography>
        )}
      </Box>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t('basic_information')}
                </Typography>
              </Box>
            </Grid>

            {/* Name - ✅ Updated helper text */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('product_name')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('name')}
                helperText={shouldShowError('name') ? errors.name : 'Max 300 characters'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 300 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('name') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('name') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* SKU */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('sku')}
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('sku')}
                helperText={shouldShowError('sku') ? errors.sku : '3-30 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ 
                  style: { textTransform: 'uppercase' },
                  maxLength: 30
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('sku') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('sku') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Barcode */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('barcode')}
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('barcode')}
                helperText={shouldShowError('barcode') ? errors.barcode : '3-30 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 30 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate barcode">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setFormData(prev => ({ ...prev, barcode: generateBarcode() }))
                          }
                          disabled={loading}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          Generate
                        </Button>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('barcode') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('barcode') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Description - ✅ Updated helper text and maxLength */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('description')}
                helperText={shouldShowError('description') ? errors.description : 'Max 500 characters (optional)'}
                disabled={loading}
                multiline
                rows={3}
                size="medium"
                inputProps={{ maxLength: 500 }}
                placeholder={t('description_placeholder')}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('description') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('description') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={loading}
                options={categoryOptions}
                value={categoryOptions.find(o => !o.isAddNew && o.id === formData.categoryId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    handleInputChange({ target: { name: 'categoryId', value: '', type: 'text', checked: false } });
                    return;
                  }
                  if (newValue.isAddNew) { setOpenCategoryDialog(true); }
                  else { handleInputChange({ target: { name: 'categoryId', value: newValue.id, type: 'text', checked: false } }); }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="medium"
                    label="Category *"
                    error={shouldShowError('categoryId')}
                    helperText={shouldShowError('categoryId') ? errors.categoryId : undefined}
                    placeholder="Search category..."
                    onBlur={() => handleFieldBlur({ target: { name: 'categoryId' } })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
                noOptionsText="No categories available"
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>

            {/* Supplier */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={loading}
                options={supplierOptions}
                value={supplierOptions.find(o => !o.isAddNew && o.id === formData.supplierId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    handleInputChange({ target: { name: 'supplierId', value: '', type: 'text', checked: false } });
                    return;
                  }
                  if (newValue.isAddNew) { setOpenSupplierDialog(true); }
                  else { handleInputChange({ target: { name: 'supplierId', value: newValue.id, type: 'text', checked: false } }); }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="medium"
                    label="Supplier *"
                    error={shouldShowError('supplierId')}
                    helperText={shouldShowError('supplierId') ? errors.supplierId : undefined}
                    placeholder="Search supplier..."
                    onBlur={() => handleFieldBlur({ target: { name: 'supplierId' } })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
                noOptionsText="No suppliers available"
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>

            {/* Brand */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={loading}
                options={brandOptions}
                value={brandOptions.find(o => !o.isAddNew && o.id === formData.productBrandId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    handleInputChange({ target: { name: 'productBrandId', value: '', type: 'text', checked: false } });
                    return;
                  }
                  if (newValue.isAddNew) { setOpenBrandDialog(true); }
                  else { handleInputChange({ target: { name: 'productBrandId', value: newValue.id, type: 'text', checked: false } }); }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="medium"
                    label="Brand *"
                    error={shouldShowError('productBrandId')}
                    helperText={shouldShowError('productBrandId') ? errors.productBrandId : undefined}
                    placeholder="Search brand..."
                    onBlur={() => handleFieldBlur({ target: { name: 'productBrandId' } })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
                noOptionsText="No brands available"
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>

            {/* Model Number - ✅ Updated helper text and maxLength */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('model_number')}
                name="modelNumber"
                value={formData.modelNumber}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('modelNumber')}
                helperText={shouldShowError('modelNumber') ? errors.modelNumber : 'Max 200 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 200 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('modelNumber') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('modelNumber') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Average Price */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('average_price')}
                name="averagePrice"
                type="number"
                value={formData.averagePrice}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('averagePrice')}
                helperText={shouldShowError('averagePrice') ? errors.averagePrice : '0 - 100,000 (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('averagePrice') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('averagePrice') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* ========== PRICING & STOCK SECTION (show before config if no variants/serial) ========== */}
            {!formData.hasVariants && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid',
                    borderColor: 'primary.main'
                  }}>
                    <EuroIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'primary.main',
                        fontSize: '1.2rem'
                      }}
                    >
                      Pricing & Stock
                    </Typography>
                  </Box>
                </Grid>

                {/* Cost Price */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('cost_price')}
                    name="costPrice"
                    type="number"
                    value={formData.costPrice}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    error={shouldShowError('costPrice')}
                    helperText={shouldShowError('costPrice') ? errors.costPrice : '0 - 100,000'}
                    required
                    disabled={loading}
                    size="medium"
                    inputProps={{ min: 0, max: 100000, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 1.5,
                        '& fieldset': {
                          borderColor: shouldShowError('costPrice') ? 'error.main' : undefined,
                          borderWidth: shouldShowError('costPrice') ? 2 : 1,
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Sale Price */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('sale_price')}
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    error={shouldShowError('price')}
                    helperText={shouldShowError('price') ? errors.price : '0 - 100,000'}
                    required
                    disabled={loading}
                    size="medium"
                    inputProps={{ min: 0, max: 100000, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 1.5,
                        '& fieldset': {
                          borderColor: shouldShowError('price') ? 'error.main' : undefined,
                          borderWidth: shouldShowError('price') ? 2 : 1,
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Min Sale Price */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('min_sale_price')}
                    name="minSalePrice"
                    type="number"
                    value={formData.minSalePrice}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    error={shouldShowError('minSalePrice')}
                    helperText={shouldShowError('minSalePrice') ? errors.minSalePrice : '0 - 100,000 (optional)'}
                    disabled={loading}
                    size="medium"
                    inputProps={{ min: 0, max: 100000, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 1.5,
                        '& fieldset': {
                          borderColor: shouldShowError('minSalePrice') ? 'error.main' : undefined,
                          borderWidth: shouldShowError('minSalePrice') ? 2 : 1,
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Stock Quantity */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('stock_quantity')}
                    name="stockQuantity"
                    type="number"
                    value={formData.stockQuantity}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    error={shouldShowError('stockQuantity')}
                    helperText={shouldShowError('stockQuantity') ? errors.stockQuantity : '0 - 100,000'}
                    required
                    disabled={loading}
                    size="medium"
                    inputProps={{ min: 0, max: 100000, step: 1 }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 1.5,
                        '& fieldset': {
                          borderColor: shouldShowError('stockQuantity') ? 'error.main' : undefined,
                          borderWidth: shouldShowError('stockQuantity') ? 2 : 1,
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Min Stock Level */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('min_stock_level')}
                    name="minStockLevel"
                    type="number"
                    value={formData.minStockLevel}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    error={shouldShowError('minStockLevel')}
                    helperText={shouldShowError('minStockLevel') ? errors.minStockLevel : 'Reorder threshold (optional)'}
                    disabled={loading}
                    size="medium"
                    inputProps={{ min: 0, max: 100000, step: 1 }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 1.5,
                        '& fieldset': {
                          borderColor: shouldShowError('minStockLevel') ? 'error.main' : undefined,
                          borderWidth: shouldShowError('minStockLevel') ? 2 : 1,
                        }
                      }
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Serial Numbers Section for Non-Variant Products (under toggle) */}
            <Collapse in={formData.isSerializedProduct && !formData.hasVariants} timeout="auto" unmountOnExit>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      SERIAL NUMBERS / IMEI
                    </Typography>
                  </Divider>
                </Grid>
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        {/* Radio buttons for IMEI vs Serial Number */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                            Select Type
                          </FormLabel>
                          <RadioGroup
                            row
                            value={serialType}
                            onChange={(e) => setSerialType(e.target.value)}
                          >
                            <FormControlLabel 
                              value="imei" 
                              control={<Radio size="small" />} 
                              label="IMEI" 
                            />
                            <FormControlLabel 
                              value="serial" 
                              control={<Radio size="small" />} 
                              label="Serial Number" 
                            />
                          </RadioGroup>
                        </FormControl>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <TextField
                            fullWidth
                            label={serialType === 'imei' ? 'Enter IMEI' : 'Enter Serial Number'}
                            value={serialInput}
                            onChange={(e) => setSerialInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && serialInput.trim()) {
                                e.preventDefault();
                                if (!serialNumbers.some(s => s.value === serialInput.trim())) {
                                  setSerialNumbers(prev => [...prev, { value: serialInput.trim(), type: serialType }]);
                                  setSerialInput('');
                                } else {
                                  toast.warning(`${serialType === 'imei' ? 'IMEI' : 'Serial number'} already added`);
                                  setSerialInput(''); // Clear input on duplicate
                                }
                              }
                            }}
                            placeholder="Type and press Enter"
                            size="small"
                            disabled={loading}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <PhoneAndroidIcon fontSize="small" color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              if (serialInput.trim()) {
                                if (!serialNumbers.some(s => s.value === serialInput.trim())) {
                                  setSerialNumbers(prev => [...prev, { value: serialInput.trim(), type: serialType }]);
                                  setSerialInput('');
                                } else {
                                  toast.warning(`${serialType === 'imei' ? 'IMEI' : 'Serial number'} already added`);
                                  setSerialInput(''); // Clear input on duplicate
                                }
                              }
                            }}
                            disabled={!serialInput.trim() || loading}
                            sx={{ minWidth: 100 }}
                          >
                            Add
                          </Button>
                        </Box>
                        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                          {serialNumbers.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                              No {serialType === 'imei' ? 'IMEI' : 'serial number'}s added yet
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {serialNumbers.map((sn, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    bgcolor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                      {idx + 1}. {sn.value || sn}
                                    </Typography>
                                    {sn.type && (
                                      <Chip 
                                        label={sn.type === 'imei' ? 'IMEI' : 'Serial'} 
                                        size="small" 
                                        sx={{ height: 18, fontSize: '0.65rem', ml: 1 }} 
                                        color={sn.type === 'imei' ? 'primary' : 'secondary'}
                                      />
                                    )}
                                  </Box>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setSerialNumbers(prev => prev.filter((_, i) => i !== idx))}
                                    disabled={loading}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Total: {serialNumbers.length} serial number(s)
                        </Typography>
                      </Paper>
                    </Grid>
              </Grid>
            </Collapse>

            {/* ========== PRODUCT CONFIGURATION SECTION ========== */}
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                pb: 1,
                borderBottom: '2px solid',
                borderColor: 'primary.main'
              }}>
                <StyleIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'primary.main',
                    fontSize: '1.2rem'
                  }}
                >
                  Product Configuration
                </Typography>
              </Box>
            </Grid>

            {/* Has Variants Toggle */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={formData.hasVariants ? 4 : 1} 
                sx={{ 
                  p: 2.5, 
                  border: '2px solid',
                  borderColor: formData.hasVariants ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  background: formData.hasVariants 
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)'
                    : 'transparent',
                  transform: formData.hasVariants ? 'scale(1.02)' : 'scale(1)'
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hasVariants}
                      onChange={(e) => {
                        setFormData(prev => ({ 
                          ...prev, 
                          hasVariants: e.target.checked,
                          isSerializedProduct: e.target.checked ? false : prev.isSerializedProduct
                        }));
                        if (!e.target.checked) {
                          setDraftVariants([]);
                        }
                      }}
                      disabled={loading || isEditMode}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={700} color="primary.main">
                        Product has Variants
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enable for products with different colors, sizes, or configurations
                      </Typography>
                      {isEditMode && (
                        <Typography variant="caption" display="block" color="warning.main" fontWeight={600}>
                          ⚠ Cannot change in edit mode
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Paper>
            </Grid>

            {/* Is Serialized Product Toggle */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={formData.isSerializedProduct ? 4 : 1} 
                sx={{ 
                  p: 2.5, 
                  border: '2px solid',
                  borderColor: formData.isSerializedProduct ? 'success.main' : 'divider',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  background: formData.isSerializedProduct 
                    ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(129, 199, 132, 0.08) 100%)'
                    : 'transparent',
                  transform: formData.isSerializedProduct ? 'scale(1.02)' : 'scale(1)',
                  opacity: formData.hasVariants ? 0.6 : 1
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSerializedProduct}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, isSerializedProduct: e.target.checked }));
                        if (!e.target.checked) {
                          setSerialNumbers([]);
                        }
                      }}
                      disabled={loading || formData.hasVariants}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={700} color="success.main">
                        Track Serial Numbers/IMEI
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enable for items like phones, laptops needing individual tracking
                      </Typography>
                    </Box>
                  }
                />
                {formData.hasVariants && (
                  <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
                    <Typography variant="caption">
                      Serial tracking is configured per variant
                    </Typography>
                  </Alert>
                )}
              </Paper>
            </Grid>



            {/* ========== INLINE VARIANTS SECTION ========== */}
            <Collapse in={formData.hasVariants} timeout="auto" unmountOnExit>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    mb: 1.5, mt: 2, pb: 1.5, borderBottom: '2px solid', borderColor: 'primary.light'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}>
                        Product Variants
                      </Typography>
                      {!isEditMode && draftVariants.length > 0 && (
                        <Chip label={draftVariants.length} size="small" color="primary" />
                      )}
                      {isEditMode && productVariantsData?.variants?.length > 0 && (
                        <Chip label={productVariantsData.variants.length} size="small" color="primary" />
                      )}
                    </Box>
                    {/* Gear icon for managing variant attribute definitions */}
                    {!isEditMode && (
                      <Tooltip title="Manage variant attributes (Color, Size, etc.)">
                        <IconButton
                          size="small"
                          onClick={() => setAttrDialogOpen(true)}
                          sx={{
                            border: '1px solid',
                            borderColor: variantAttributes.length > 0 ? 'primary.main' : 'divider',
                            color: variantAttributes.length > 0 ? 'primary.main' : 'action.active',
                          }}
                        >
                          <Badge
                            badgeContent={variantAttributes.length || null}
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}
                          >
                            <SettingsIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    )}
                    {isEditMode && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => { setSelectedVariant(null); setVariantDialogOpen(true); }}
                        disabled={loading}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        Add Variant
                      </Button>
                    )}
                  </Box>
                </Grid>

            {/* Add-mode: info banner */}
            {!isEditMode && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  Variants are saved together with the product. Add all variants below, then click <strong>Create Product</strong>.
                </Alert>
              </Grid>
            )}

            {/* Error loading variant attributes */}
            {!isEditMode && variantAttrsError && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Unable to load variant attributes: {variantAttrsError}. You can still add variants manually using the Attributes field.
                </Alert>
              </Grid>
            )}

            {/* Info: No attributes defined yet */}
            {!isEditMode && !variantAttrsError && variantAttributes.length === 0 && !variantAttrsLoading && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  No variant attributes defined yet. Click the <strong>gear icon (⚙️)</strong> above to create attributes like Color, Size, Storage, etc. 
                  Once created, they will appear as dropdown options when adding variants.
                </Alert>
              </Grid>
            )}

            {/* Add-mode: Variant Input Form */}
            {!isEditMode && (
              <>
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 3, border: '2px solid', borderColor: 'primary.light', borderRadius: 2 }}>
                    {/* Dynamic Header - Shows variant name when attributes selected */}
                    <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      {currentVariant.variantName ? (
                        <Typography variant="h6" fontWeight={600} color="primary">
                          {currentVariant.variantName}
                        </Typography>
                      ) : (
                        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                          {isEditingVariant ? 'Edit Variant' : 'Configure Variant Attributes'}
                        </Typography>
                      )}
                      {currentVariant.variantName && (
                        <Typography variant="caption" color="text.secondary">
                          {isEditingVariant ? 'Editing variant' : 'Adding new variant'}
                        </Typography>
                      )}
                    </Box>
                    
                    <Grid container spacing={2}>
                      {/* Row 1: Multi-attribute selection UI (MOVED TO TOP) */}
                      <Grid item xs={12}>
                        {variantAttributes.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" fontWeight={600} color="text.secondary">
                                Select Attribute Values (from database)
                              </Typography>
                              <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                                {variantAttributes.length} attribute{variantAttributes.length > 1 ? 's' : ''} available
                              </Typography>
                            </Box>
                            {variantAttributes.map((attr) => {
                              console.log('Rendering attribute dropdown:', attr);
                              return (
                                <FormControl key={attr.id} fullWidth size="small">
                                  <InputLabel>{attr.name}</InputLabel>
                                  <Select
                                    value={currentVariant.attributes?.[attr.name] || ''}
                                    label={attr.name}
                                    onChange={e => {
                                      const newAttributes = {
                                        ...(typeof currentVariant.attributes === 'object' && currentVariant.attributes !== null ? currentVariant.attributes : {}),
                                        [attr.name]: e.target.value
                                      };
                                      // Auto-generate variant name from selected attributes
                                      const variantNameParts = Object.values(newAttributes).filter(Boolean);
                                      const autoGeneratedName = variantNameParts.length > 0 
                                        ? variantNameParts.join(' / ') 
                                        : '';
                                      
                                      setCurrentVariant(prev => ({
                                        ...prev,
                                        attributes: newAttributes,
                                        variantName: autoGeneratedName
                                      }));
                                    }}
                                  >
                                    {attr.values && attr.values.length > 0 ? (
                                      attr.values.map((val, idx) => (
                                        <MenuItem key={idx} value={val}>{val}</MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem disabled>No values available</MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                              );
                            })}
                          </Box>
                        ) : (
                          <TextField
                            fullWidth
                            label="Attributes"
                            value={typeof currentVariant.attributes === 'string' ? currentVariant.attributes : ''}
                            onChange={(e) => setCurrentVariant({ ...currentVariant, attributes: e.target.value })}
                            placeholder="e.g., Color: Blue, Size: Large"
                            helperText="Optional: Comma-separated attributes (or manage attributes via the gear icon above)"
                            size="small"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                        )}
                      </Grid>

                      {/* Row 2: SKU & Barcode */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="SKU *"
                          value={currentVariant.sku}
                          onChange={(e) => setCurrentVariant({ ...currentVariant, sku: e.target.value })}
                          placeholder="Unique SKU code"
                          error={!currentVariant.sku.trim()}
                          helperText={!currentVariant.sku.trim() ? 'Required' : 'Unique identifier'}
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Generate SKU">
                                  <IconButton
                                    size="small"
                                    onClick={() => setCurrentVariant({ ...currentVariant, sku: generateBarcode() })}
                                  >
                                    <QrCodeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Barcode"
                          value={currentVariant.barCode}
                          onChange={(e) => setCurrentVariant({ ...currentVariant, barCode: e.target.value })}
                          placeholder="Optional barcode"
                          helperText="Optional"
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Generate Barcode">
                                  <IconButton
                                    size="small"
                                    onClick={() => setCurrentVariant({ ...currentVariant, barCode: generateBarcode() })}
                                  >
                                    <QrCodeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>

                      {/* Row 3: Pricing */}
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Cost Price *"
                          type="number"
                          value={currentVariant.costPrice}
                          onChange={(e) => setCurrentVariant({ ...currentVariant, costPrice: e.target.value })}
                          error={!currentVariant.costPrice}
                          helperText={!currentVariant.costPrice ? 'Required' : 'Purchase cost'}
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Sale Price *"
                          type="number"
                          value={currentVariant.salePrice}
                          onChange={(e) => setCurrentVariant({ ...currentVariant, salePrice: e.target.value })}
                          error={!currentVariant.salePrice}
                          helperText={!currentVariant.salePrice ? 'Required' : 'Selling price'}
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Min Sale Price"
                          type="number"
                          value={currentVariant.minSalePrice}
                          onChange={(e) => setCurrentVariant({ ...currentVariant, minSalePrice: e.target.value })}
                          helperText="Minimum allowed price"
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>

                      {/* Row 4: Stock */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Stock Quantity"
                          type="number"
                          value={currentVariant.stockQuantity}
                          onChange={(e) => setCurrentVariant({ ...currentVariant, stockQuantity: e.target.value })}
                          helperText="Available quantity"
                          size="small"
                          inputProps={{ min: 0, step: 1 }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Min Stock Level"
                          type="number"
                          value={currentVariant.minStockLevel}
                          onChange={(e) => setCurrentVariant({ ...currentVariant, minStockLevel: e.target.value })}
                          helperText="Reorder threshold"
                          size="small"
                          inputProps={{ min: 0, step: 1 }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>

                      {/* Serial Number Section */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                          <Button
                            variant={showVariantSerialInput ? 'contained' : 'outlined'}
                            startIcon={<PhoneAndroidIcon />}
                            onClick={() => setShowVariantSerialInput(!showVariantSerialInput)}
                            disabled={!isCurrentVariantValid()}
                            size="small"
                            sx={{ textTransform: 'none' }}
                          >
                            {showVariantSerialInput ? 'Hide' : 'Add'} Serial Numbers / IMEI
                          </Button>
                          {currentVariant.imeiItems.length > 0 && (
                            <Chip 
                              label={`${currentVariant.imeiItems.length} item(s)`} 
                              size="small" 
                              color="success" 
                            />
                          )}
                        </Box>

                        <Collapse in={showVariantSerialInput} timeout="auto">
                          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            {/* Radio buttons for IMEI vs Serial Number */}
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                                Select Type
                              </FormLabel>
                              <RadioGroup
                                row
                                value={currentVariant.serialType}
                                onChange={(e) => setCurrentVariant({ ...currentVariant, serialType: e.target.value })}
                              >
                                <FormControlLabel 
                                  value="imei" 
                                  control={<Radio size="small" />} 
                                  label="IMEI" 
                                />
                                <FormControlLabel 
                                  value="serial" 
                                  control={<Radio size="small" />} 
                                  label="Serial Number" 
                                />
                              </RadioGroup>
                            </FormControl>
                            
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={currentVariant.serialType === 'imei' ? 'Enter IMEI' : 'Enter Serial Number'}
                                value={variantSerialInput}
                                onChange={(e) => setVariantSerialInput(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddVariantSerial();
                                  }
                                }}
                                placeholder={`Enter ${currentVariant.serialType === 'imei' ? 'IMEI' : 'serial number'}`}
                              />
                              <Button
                                variant="contained"
                                onClick={handleAddVariantSerial}
                                disabled={!variantSerialInput.trim()}
                                sx={{ minWidth: 100 }}
                              >
                                Add
                              </Button>
                            </Box>
                            {currentVariant.imeiItems.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                                No {currentVariant.serialType === 'imei' ? 'IMEI' : 'serial number'}s added
                              </Typography>
                            ) : (
                              <Stack spacing={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                {currentVariant.imeiItems.map((serial, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1,
                                      bgcolor: 'grey.50',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" fontWeight={500}>
                                        {idx + 1}. {serial.value || serial.imeiNumber || serial}
                                      </Typography>
                                      {serial.type && (
                                        <Chip 
                                          label={serial.type === 'imei' ? 'IMEI' : 'Serial'} 
                                          size="small" 
                                          sx={{ height: 18, fontSize: '0.65rem' }} 
                                          color={serial.type === 'imei' ? 'primary' : 'secondary'}
                                        />
                                      )}
                                    </Box>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveVariantSerial(idx)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </Paper>
                        </Collapse>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                          {isEditingVariant && (
                            <Button
                              variant="outlined"
                              onClick={resetCurrentVariant}
                              sx={{ textTransform: 'none' }}
                            >
                              Cancel
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            startIcon={isEditingVariant ? <EditIcon /> : <AddIcon />}
                            onClick={handleAddOrUpdateVariant}
                            disabled={!isCurrentVariantValid()}
                            sx={{ textTransform: 'none', minWidth: 140 }}
                          >
                            {isEditingVariant ? 'Update Variant' : 'Add Variant'}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Added Variants List */}
                <Grid item xs={12}>
                  {draftVariants.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                      <CategoryIcon sx={{ fontSize: 36, mb: 1, opacity: 0.35 }} />
                      <Typography variant="body2" color="text.secondary">
                        No variants added yet. Fill in the form above to add variants.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {draftVariants.map((variant, idx) => (
                        <Paper 
                          key={idx} 
                          elevation={2} 
                          sx={{ 
                            border: '2px solid', 
                            borderColor: 'divider', 
                            borderRadius: 2,
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: 4,
                              borderColor: 'primary.light'
                            }
                          }}
                        >
                          {/* Variant Line Item */}
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between' 
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StyleIcon color="primary" />
                                <Box>
                                  <Typography variant="subtitle1" fontWeight={700}>
                                    {variant.variantName}
                                  </Typography>
                                  {variant.attributes && (
                                    <Typography variant="caption" color="text.secondary">
                                      {typeof variant.attributes === 'object' 
                                        ? Object.entries(variant.attributes).map(([k, v]) => `${k}: ${v}`).join(', ')
                                        : variant.attributes
                                      }
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Chip label={`SKU: ${variant.sku}`} size="small" color="default" />
                              <Chip label={`â‚¬${parseFloat(variant.salePrice || 0).toFixed(2)}`} size="small" color="success" />
                              {variant.stockQuantity && (
                                <Chip label={`Stock: ${variant.stockQuantity}`} size="small" color="info" />
                              )}
                              {variant.imeiItems?.length > 0 && (
                                <Chip 
                                  icon={<PhoneAndroidIcon />} 
                                  label={`${variant.imeiItems.length} Serial(s)`} 
                                  size="small" 
                                  color="warning" 
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Edit Variant">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditVariant(idx)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Variant">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteVariant(idx)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* Serial Numbers Sub-Items */}
                          {variant.imeiItems?.length > 0 && (
                            <Box sx={{ bgcolor: 'background.paper', px: 6, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                SERIAL NUMBERS / IMEI ({variant.imeiItems.length})
                              </Typography>
                              <Stack spacing={0.5}>
                                {variant.imeiItems.map((serial, serialIdx) => (
                                  <Box
                                    key={serialIdx}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1,
                                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {serialIdx + 1}. {serial.value || serial.imeiNumber || serial}
                                      </Typography>
                                      {serial.type && (
                                        <Chip 
                                          label={serial.type === 'imei' ? 'IMEI' : 'Serial'} 
                                          size="small" 
                                          sx={{ height: 16, fontSize: '0.6rem' }} 
                                          color={serial.type === 'imei' ? 'primary' : 'secondary'}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Grid>
              </>
            )}

            {/* Edit-mode: Redux-managed variant cards */}
            {isEditMode && (
              <Grid item xs={12}>
                {variantsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={26} />
                  </Box>
                ) : !productVariantsData?.variants?.length ? (
                  <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                    <CategoryIcon sx={{ fontSize: 36, mb: 1, opacity: 0.35 }} />
                    <Typography variant="body2" color="text.secondary">No variants yet.</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={1.5}>
                    {productVariantsData.variants.map((v) => {
                      const stock = v.availableStock ?? v.stockQuantity ?? 0;
                      const stockColor = stock <= 0 ? 'error' : stock <= (v.minStockLevel || 5) ? 'warning' : 'success';
                      return (
                        <Grid item xs={12} sm={6} md={4} key={v.id}>
                          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ pb: '10px !important', pt: 1.5, px: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: '68%' }}>
                                  {v.variantName}
                                </Typography>
                                <Stack direction="row" spacing={0.25}>
                                  <IconButton size="small" onClick={() => { setSelectedVariant(v); setVariantDialogOpen(true); }}>
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => setConfirmDeleteVariant(v)}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Stack>
                              </Stack>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                SKU: {v.sku}
                              </Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Chip label={`€${Number(v.salePrice || 0).toFixed(2)}`} size="small" color="primary" variant="outlined" />
                                <Chip
                                  icon={<InventoryIcon sx={{ fontSize: 11 }} />}
                                  label={`${stock} in stock`}
                                  size="small" color={stockColor}
                                />
                                {v.isSerializedProduct && (
                                  <Chip icon={<QrCodeIcon sx={{ fontSize: 11 }} />} label="IMEI" size="small" color="info" />
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </Collapse>

            {/* Additional Settings Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  Additional Settings
                </Typography>
              </Box>
            </Grid>

            {/* Image URL */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('image_url')}
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('imageUrl')}
                helperText={shouldShowError('imageUrl') ? errors.imageUrl : 'Valid URL starting with http:// or https:// (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 300 }}
                placeholder="https://example.com/image.jpg"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('imageUrl') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('imageUrl') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={3}>
              <FormControl 
                fullWidth 
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleInputChange}
                  label={t('status')}
                >
                  <MenuItem value={true}>{t('active')}</MenuItem>
                  <MenuItem value={false}>{t('inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Promotion Product Toggle */}
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    name="isPromotionProduct"
                    checked={formData.isPromotionProduct}
                    onChange={handleInputChange}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Promotion Product
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mark as promotional item
                    </Typography>
                  </Box>
                }
                sx={{ 
                  '& .MuiFormControlLabel-label': { ml: 1 },
                  alignItems: 'flex-start',
                  mt: 1 
                }}
              />
            </Grid>

            {/* Promotion Discount */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('promotion_discount')}
                name="promotionDiscount"
                type="number"
                value={formData.promotionDiscount}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('promotionDiscount')}
                helperText={shouldShowError('promotionDiscount') ? errors.promotionDiscount : 'Discount % (0-100)'}
                disabled={loading || !formData.isPromotionProduct}
                size="medium"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    backgroundColor: !formData.isPromotionProduct ? 'action.disabled' : 'inherit',
                    '& fieldset': {
                      borderColor: shouldShowError('promotionDiscount') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('promotionDiscount') ? 2 : 1,
                    }
                  }
                }}
                placeholder={formData.isPromotionProduct ? "Enter %" : "Enable promotion"}
              />
            </Grid>

            {/* Server Error Message */}
            {serverError && (
              <Grid item xs={12}>
                <Box
                  id="server-error-message"
                  sx={{
                    p: 2,
                    backgroundColor: '#ffebee',
                    borderRadius: 1,
                    border: '1px solid #ef5350',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 500,
                      flex: 1
                    }}
                  >
                    {serverError}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setServerError('')}
                    sx={{ color: '#d32f2f' }}
                  >
                    ×
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end', 
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                {isEditMode && hasFormChanged && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleResetForm}
                    disabled={loading}
                    sx={{ 
                      minWidth: 120,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Reset
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isButtonDisabled}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2,
                    opacity: isButtonDisabled ? 0.6 : 1
                  }}
                >
                  {loading 
                    ? (isEditMode ? 'Updating...' : 'Creating...') 
                    : (isEditMode ? 'Update Product' : 'Create Product')
                  }
                </Button>
              </Box>
              
              {isEditMode && !hasFormChanged && !loading && (
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    Make changes to enable update
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Edit-mode: VariantFormDialog (saves directly to API via Redux) */}
      <VariantFormDialog
        open={variantDialogOpen}
        onClose={() => setVariantDialogOpen(false)}
        productId={isEditMode ? Number(id) : null}
        variant={selectedVariant}
        onSaved={() => {
          setVariantDialogOpen(false);
          dispatch(fetchVariantsByProduct(Number(id)));
        }}
      />

      {/* Edit-mode: confirm delete variant */}
      <ConfirmationDialog
        open={Boolean(confirmDeleteVariant)}
        title="Delete Variant"
        message={`Are you sure you want to delete "${confirmDeleteVariant?.variantName}"? This cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        onConfirm={async () => {
          if (confirmDeleteVariant) {
            await dispatch(removeVariant({ id: confirmDeleteVariant.id, productId: Number(id) }));
            setConfirmDeleteVariant(null);
          }
        }}
        onCancel={() => setConfirmDeleteVariant(null)}
      />

      {/* Add-mode: confirm delete draft variant */}
      <ConfirmationDialog
        open={confirmDeleteDraftVariant !== null}
        title="Delete Variant"
        message={`Are you sure you want to delete variant "${confirmDeleteDraftVariant !== null ? draftVariants[confirmDeleteDraftVariant]?.variantName || 'Unnamed' : ''}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        onConfirm={confirmDeleteDraft}
        onCancel={() => setConfirmDeleteDraftVariant(null)}
      />

      {/* Category Dialog */}
      <Dialog 
        open={openCategoryDialog} 
        onClose={() => setOpenCategoryDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('category_name')}
            fullWidth
            required
            value={newCategory.name}
            onChange={(e) => setNewCategory({ name: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenCategoryDialog(false);
              setNewCategory({ name: '' });
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCategory} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog 
        open={openSupplierDialog} 
        onClose={() => {
          setOpenSupplierDialog(false);
          setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
          setSupplierFormTouched(false);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Supplier</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Supplier Name *"
            fullWidth
            required
            value={newSupplier.name}
            onChange={(e) => {
              setNewSupplier({ ...newSupplier, name: e.target.value });
              if (supplierFormTouched && e.target.value.trim()) {
                setSupplierFormTouched(false);
              }
            }}
            onBlur={() => {
              if (!newSupplier.name.trim()) {
                setSupplierFormTouched(true);
              }
            }}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={supplierFormTouched && !newSupplier.name.trim() ? "Supplier name is required" : "Required field"}
            error={supplierFormTouched && !newSupplier.name.trim()}
          />
          <TextField
            margin="dense"
            label={t('contact_person')}
            fullWidth
            value={newSupplier.contactPerson || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
          <TextField
            margin="dense"
            label={t('email')}
            type="email"
            fullWidth
            value={newSupplier.email || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
          <TextField
            margin="dense"
            label={t('phone')}
            fullWidth
            value={newSupplier.phone || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
          <TextField
            margin="dense"
            label={t('address')}
            fullWidth
            multiline
            rows={2}
            value={newSupplier.address || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenSupplierDialog(false);
              setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
              setSupplierFormTouched(false);
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddSupplier} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
            disabled={!newSupplier.name.trim()} // ✅ Disable if name is empty
          >
            Add Supplier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog 
        open={openBrandDialog} 
        onClose={() => {
          setOpenBrandDialog(false);
          setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
          setBrandFormTouched(false);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Brand</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Brand Name *"
            fullWidth
            required
            value={newBrand.BrandName}
            onChange={(e) => {
              setNewBrand({ ...newBrand, BrandName: e.target.value });
              if (brandFormTouched && e.target.value.trim()) {
                setBrandFormTouched(false);
              }
            }}
            onBlur={() => {
              if (!newBrand.BrandName.trim()) {
                setBrandFormTouched(true);
              }
            }}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={brandFormTouched && !newBrand.BrandName.trim() ? "Brand name is required" : "Required field"}
            error={brandFormTouched && !newBrand.BrandName.trim()}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            margin="dense"
            label={t('description')}
            fullWidth
            multiline
            rows={3}
            value={newBrand.Description || ''}
            onChange={(e) => setNewBrand({ ...newBrand, Description: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText="Optional (max 500 characters)"
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            margin="dense"
            label="Logo URL"
            fullWidth
            value={newBrand.LogoUrl || ''}
            onChange={(e) => setNewBrand({ ...newBrand, LogoUrl: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText="Optional (e.g., https://example.com/logo.png)"
            placeholder="https://example.com/logo.png"
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            margin="dense"
            label="Website"
            fullWidth
            value={newBrand.Website || ''}
            onChange={(e) => setNewBrand({ ...newBrand, Website: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText="Optional (e.g., https://example.com)"
            placeholder="https://example.com"
            inputProps={{ maxLength: 200 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenBrandDialog(false);
              setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
              setBrandFormTouched(false);
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddBrand} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
            disabled={!newBrand.BrandName.trim()}
          >
            Add Brand
          </Button>
        </DialogActions>
      </Dialog>

      {/* Variant Attributes Management Dialog */}
      <Dialog 
        open={attrDialogOpen} 
        onClose={() => {
          setAttrDialogOpen(false);
          setNewAttrForm({ name: '', values: '' });
          setAttrEditForm({ id: null, name: '', values: '' });
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            Manage Variant Attributes
          </Box>
          <Tooltip title="Refresh attributes from database">
            <IconButton
              size="small"
              onClick={() => {
                hasFetchedAttributesRef.current = false;
                dispatch(fetchVariantAttributes());
              }}
              disabled={variantAttrsLoading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Define attribute types (like Color, Size, Storage) and their possible values. 
            Click <strong>Add</strong> to save each attribute to the database. 
            Once saved, these attributes will automatically appear as dropdown options when creating variants below.
          </Alert>

          {variantAttrsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {variantAttrsError}
            </Alert>
          )}

          {/* Add New Attribute */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Add New Attribute
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Attribute Name *"
                  value={newAttrForm.name}
                  onChange={(e) => setNewAttrForm({ ...newAttrForm, name: e.target.value })}
                  placeholder="e.g., Color, Size"
                  helperText="e.g., Color, Size, Material"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Values (comma-separated) *"
                  value={newAttrForm.values}
                  onChange={(e) => setNewAttrForm({ ...newAttrForm, values: e.target.value })}
                  placeholder="e.g., Red, Blue, Green"
                  helperText="Separate values with commas"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={async () => {
                    if (newAttrForm.name.trim() && newAttrForm.values.trim()) {
                      const values = newAttrForm.values.split(',').map(v => v.trim()).filter(Boolean);
                      const attributeData = {
                        AttributeName: newAttrForm.name.trim(),
                        AttributeCode: null,
                        Description: null,
                        DisplayOrder: 0,
                        IsRequired: false,
                        IsActive: true,
                        Values: values.map((val, idx) => ({
                          Value: val,
                          ValueCode: null,
                          ColorHex: null,
                          DisplayOrder: idx,
                          IsActive: true
                        }))
                      };
                      
                      try {
                        await dispatch(createVariantAttribute(attributeData)).unwrap();
                        setNewAttrForm({ name: '', values: '' });
                        toast.success(`Attribute "${attributeData.AttributeName}" added! It will now appear in the variant form dropdowns.`, {
                          autoClose: 4000
                        });
                      } catch (error) {
                        console.error('Error creating attribute:', error);
                        const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to add attribute');
                        toast.error(errorMsg);
                      }
                    }
                  }}
                  disabled={!newAttrForm.name.trim() || !newAttrForm.values.trim() || variantAttrsLoading}
                  sx={{ height: 40 }}
                >
                  {variantAttrsLoading ? 'Adding...' : 'Add'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Existing Attributes List */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Defined Attributes ({variantAttributes.length})
          </Typography>
          {variantAttrsLoading && variantAttributes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Loading attributes...
              </Typography>
            </Box>
          ) : variantAttributes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No attributes defined yet. Add your first attribute above.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {variantAttributes.map((attr) => (
                <Paper key={attr.id} elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  {attrEditForm.id === attr.id ? (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Name"
                          value={attrEditForm.name}
                          onChange={(e) => setAttrEditForm({ ...attrEditForm, name: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Values"
                          value={attrEditForm.values}
                          onChange={(e) => setAttrEditForm({ ...attrEditForm, values: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={async () => {
                              if (attrEditForm.name.trim() && attrEditForm.values.trim()) {
                                const values = attrEditForm.values.split(',').map(v => v.trim()).filter(Boolean);
                                const updateData = {
                                  AttributeName: attrEditForm.name.trim(),
                                  Values: values
                                };
                                
                                try {
                                  await dispatch(updateVariantAttribute({ id: attr.id, data: updateData })).unwrap();
                                  setAttrEditForm({ id: null, name: '', values: '' });
                                  toast.success(`Attribute "${updateData.AttributeName}" updated! Changes will reflect in variant form.`);
                                } catch (error) {
                                  console.error('Error updating attribute:', error);
                                  const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to update attribute');
                                  toast.error(errorMsg);
                                }
                              }
                            }}
                            disabled={variantAttrsLoading}
                          >
                            {variantAttrsLoading ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setAttrEditForm({ id: null, name: '', values: '' })}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body1" fontWeight={600} color="primary.main">
                          {attr.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {attr.values.map((val, idx) => (
                            <Chip key={idx} label={val} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setAttrEditForm({ id: attr.id, name: attr.name, values: attr.values.join(', ') });
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={async () => {
                            if (window.confirm(`Delete attribute "${attr.name}"?`)) {
                              try {
                                await dispatch(deleteVariantAttribute(attr.id)).unwrap();
                                toast.success(`Attribute "${attr.name}" deleted successfully`);
                              } catch (error) {
                                console.error('Error deleting attribute:', error);
                                const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to delete attribute');
                                toast.error(errorMsg);
                              }
                            }
                          }}
                          disabled={variantAttrsLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setAttrDialogOpen(false);
              setNewAttrForm({ name: '', values: '' });
              setAttrEditForm({ id: null, name: '', values: '' });
            }}
            variant="contained"
            sx={{ borderRadius: 1.5 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddProductPage;